/**
 * Suppliers directory data access for the admin SPA (hired-in gear, PR2).
 *
 * Mirrors the JSON shape of go-service/internal/api/suppliers.go: GET (list),
 * POST (create), PATCH (update), DELETE ?id= (remove) on /api/admin/suppliers.
 * The update path was retired in #308 (then unreachable) and restored in #321.
 * Its caller is now Settings → Suppliers (#333), which reopens its inline
 * name/email/phone panel pre-filled to edit a supplier in place; the equipment
 * detail page only links suppliers to an item.
 */
import { resource } from "./resource";

export interface Supplier {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
}

/** Create payload — name required, email/phone optional. */
export interface SupplierCreate {
  name: string;
  email: string | null;
  phone: string | null;
}

/** Update payload — id required; only supplied keys are written server-side. */
interface SupplierUpdate extends SupplierCreate {
  id: string;
}

const suppliers = resource<Supplier[]>("/api/admin/suppliers", "suppliers");
const supplier = resource<Supplier>("/api/admin/suppliers", "supplier");

/**
 * Fetch active suppliers (ordered by name). The server owns the soft-delete
 * filter — do not re-filter on `active` at call sites. Soft-deleted rows are
 * an endpoint opt-in (`?include_inactive=true`), unused by the SPA today.
 */
export const listSuppliers = async (
  signal?: AbortSignal,
): Promise<Supplier[]> => (await suppliers.get(signal)) ?? [];

/** Create a supplier. */
export const createSupplier = (payload: SupplierCreate): Promise<Supplier> =>
  supplier.post(payload);

/** Update a supplier in place (id in body, mirrors contractors). */
export const updateSupplier = (payload: SupplierUpdate): Promise<Supplier> =>
  supplier.patch(payload);

/** Delete a supplier by id. */
export const deleteSupplier = (id: string): Promise<void> =>
  supplier.del({ id });
